// React dependencies
import React, { useEffect, useState } from 'react';

// External Dependencies
import _ from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { TextField } from '@material-ui/core';

// Internal Dependencies
import PhotosComponent from '../../photos/photos';
import apiService from '../../../services.js/apiService';
import MText from '../../common/MText';
import MButton from '../../common/MButton';
import Colours from '../../../styles/colours';
import MapModal from '../../poi/MapModal';
import PoiMap from '../../common/poi/poiMap';
import { IfMapCoordInvalidThrowError, IsEmpty } from '../../../common/checks';
import { Trim } from '../../../common/util';
import { Status } from '../../../common/constants';
import convert from '../../../common/convert';

const defaultPhotoInfo = {
  id:       null,
  lat:      null,
  lng:      null,
  display:  '',
  original: '',
  info:     { takenOn: null, text: '' },
};

const formatPhoto = (p) => {
  if (IsEmpty(p)) {
    return defaultPhotoInfo;
  }
  return {
    id:       IsEmpty(p.id) ? defaultPhotoInfo.id : p.id,
    lat:      IsEmpty(p.lat) ? defaultPhotoInfo.lat : p.lat,
    lng:      IsEmpty(p.lng) ? defaultPhotoInfo.lng : p.lng,
    display:  IsEmpty(p.cropUrl) || p.cropUrl === p.url ? convert.imageResizeUrl(p.url, 300, 300) : p.cropUrl,
    original: IsEmpty(p.url) ? defaultPhotoInfo.original : p.url,
    info:     IsEmpty(p.info) ? defaultPhotoInfo.info : p.info,
  };
};

// Album-specific photos component using the reusable component
const AlbumPhotos = (props) => {
  const [album, setAlbum] = useState(null);
  const [photo, setPhoto] = useState(defaultPhotoInfo);
  const [isLoading, setIsLoading] = useState(false);
  const [showMapModal, setShowMapModal] = useState(false);

  const { pageId, id: albumId } = props.match.params;
  const isPhotoNotEditable = IsEmpty(album) || album.status === Status.Rejected;

  const getAlbum = async () => {
    try {
      if (!IsEmpty(pageId) && !IsEmpty(albumId)) {
        setIsLoading(true);
        const data = await apiService.getPageAlbum(pageId, albumId);

        setAlbum({ ...data, photos: _.map(data.photos, formatPhoto) });

        if (IsEmpty(data)) {
          toast.warn('Album not found');
        }
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePhotoChange = (name, value) => {
    if (['text', 'takenOn'].includes(name)) {
      setPhoto((prev) => ({ ...prev, info: { ...prev.info, [name]: value } }));
    } else {
      setPhoto((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handlePhotoForm = (formData) => {
    IfMapCoordInvalidThrowError(photo.lat, photo.lng);

    const trimmedDescription = Trim(photo.info.text);

    if (!IsEmpty(photo.info.takenOn)) {
      formData.append('takenOn', photo.info.takenOn);
    }

    if (!IsEmpty(trimmedDescription)) {
      formData.append('text', trimmedDescription);
    }

    if (!IsEmpty(photo.lat)) {
      formData.append('lat', photo.lat);
    }

    if (!IsEmpty(photo.lng)) {
      formData.append('lng', photo.lng);
    }
  };

  const handleUploadMedia = async (currentAlbumId, formData, file) => {
    // We don't use try catch here so the error can be caught by the handleSave method in PhotosComponent
    handlePhotoForm(formData);
    const albumPhoto = await apiService.uploadPageAlbumPhoto(pageId, currentAlbumId, formData, file);
    return formatPhoto(albumPhoto);
  };

  const handleUpdateMedia = async (currentAlbumId, formData, file) => {
    // We don't use try catch here so the error can be caught by the handleSave method in PhotosComponent
    if (!IsEmpty(photo.id)) {
      handlePhotoForm(formData);
      const albumPhoto = await apiService.updatePageAlbumPhoto(pageId, currentAlbumId, photo.id, formData, file);
      return formatPhoto(albumPhoto);
    }
    return null;
  };

  const handleDeleteMedia = (currentAlbumId, ph) => apiService.deletePageAlbumPhoto(pageId, currentAlbumId, ph.id);

  const onTextFieldChange = ({ target: { name, value } }) => handlePhotoChange(name, value);

  const preventWhitespace = (e) => {
    e.target.value = e.target.value.replace(' ', '');
  };

  useEffect(() => {
    getAlbum();
  }, [pageId, albumId]);

  return (
    isLoading ? (
      <MText text="Loading..." />
    ) : (
      !IsEmpty(album) && (
        <PhotosComponent
          {...props}
          disabled={isPhotoNotEditable}
          sortable={false}
          fetchEntity={() => apiService.getPageAlbum(pageId, albumId)}
          uploadMedia={handleUploadMedia}
          deleteMedia={handleDeleteMedia}
          updateMedia={handleUpdateMedia}
          cropPixelWidth={400}
          cropPixelHeight={400}
          outputPixelWidth={800}
          outputPixelHeight={800}
          itemWidth={200}
          itemHeight={200}
          currentMedia={photo}
          getMediaId={(p) => p.id}
          isUpdating={!IsEmpty(photo.id)}
          getMediaFromEntity={() => (IsEmpty(album.photos) ? [] : album.photos)}
          mediaFieldName="photos"
          title="Album Photos"
          minMediaCount={0}
          showForm
          onCropperClose={() => setPhoto(defaultPhotoInfo)}
          onSelectImageToCrop={(p) => setPhoto(p)}
          renderForm={() => (
            <form className="w-100">
              {
                !IsEmpty(photo.id) && (
                  <div className="mt-2">
                    <MText
                      size={12}
                      colour={Colours.DarkGray}
                      text={`Photo ID: ${photo.id}`}
                    />
                  </div>
                )
              }

              <div className="mt-4">
                <MText text="Taken On" />
                <TextField
                  fullWidth
                  name="takenOn"
                  placeholder="Taken On"
                  variant="outlined"
                  type="date"
                  inputProps={{
                    max:   moment().format('YYYY-MM-DD'),
                    style: { fontSize: 13 },
                  }}
                  size="small"
                  disabled={isPhotoNotEditable}
                  value={photo.info.takenOn}
                  onChange={onTextFieldChange}
                />
              </div>

              <div className="mt-4">
                <MText text="Description" />
                <TextField
                  fullWidth
                  name="text"
                  multiline
                  rows={4}
                  rowsMax={4}
                  placeholder="Description"
                  variant="outlined"
                  inputProps={{ style: { fontSize: 13 } }}
                  size="small"
                  disabled={isPhotoNotEditable}
                  value={photo.info.text}
                  onChange={onTextFieldChange}
                />
              </div>

              <div className="mt-4">
                <MText text="Location" size={18} bold />
                {
                  !isPhotoNotEditable && (
                    <MButton
                      text="Pick location from map where the photo was taken"
                      variant="text"
                      className="px-0 pt-0"
                      textColour={Colours.Blue}
                      textStyle={{ fontWeight: 500, textDecoration: 'underline' }}
                      onClick={() => setShowMapModal(true)}
                    />
                  )
                }

                <div className="form-group mt-2">
                  <MText text="Lat" />
                  <input
                    type="text"
                    className="form-control"
                    name="lat"
                    value={photo.lat}
                    onChange={onTextFieldChange}
                    onInput={preventWhitespace}
                    disabled={isPhotoNotEditable}
                  />
                </div>

                <div className="form-group">
                  <MText text="Lng" />
                  <input
                    type="text"
                    className="form-control"
                    name="lng"
                    value={photo.lng}
                    onChange={onTextFieldChange}
                    onInput={preventWhitespace}
                    disabled={isPhotoNotEditable}
                  />
                </div>

                <div className="mt-2">
                  <PoiMap
                    disabled
                    mapHeight={250}
                    coord={{ lat: photo.lat, lng: photo.lng }}
                  />
                </div>
              </div>

              <MapModal
                showMapModal={showMapModal}
                closeMapModal={() => setShowMapModal(false)}
                coord={{ lat: photo.lat, lng: photo.lng }}
                onApply={({ lat, lng }) => setPhoto((prev) => ({ ...prev, lat, lng }))}
              />
            </form>
          )}
        />
      )
    )
  );
};

AlbumPhotos.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape(Object).isRequired,
  }).isRequired,
};

export default AlbumPhotos;
